import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Card, Typography, Divider, Button } from "@mui/material";
import { useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import DiamondIcon from "@mui/icons-material/Diamond";
import {
  cancelDiamondPointOrderRequest,
  getDiamondPendingOrderRequest,
} from "../../redux/DiamondPoint/DiamondPointSlice";
import { useNavigate } from "react-router-dom";
import CloseIcon from "@mui/icons-material/Close";
import { useSelector } from "react-redux";
import { useAppTheme } from "../../theme/useAppTheme";
import useCurrency from "../../hooks/useCurrency";

export default function DiamondPointPendingOrder() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { order: routeOrder, id } = location.state || {};
  const { cancelResponse, pendingOrder, loading } = useSelector(
    (state) => state.diamondPoint,
  );
  const themeColors = useAppTheme();
  const pendingFetchStarted = useRef(false);
  const [pendingDetails, setPendingDetails] = useState(null);

  const { symbol } = useCurrency();

  useEffect(() => {
    if (!id) return;
    pendingFetchStarted.current = true;
    dispatch(getDiamondPendingOrderRequest({ id }));
  }, [id, dispatch]);

  useEffect(() => {
    if (!pendingFetchStarted.current || loading) return;
    if (pendingOrder && typeof pendingOrder === "object") {
      setPendingDetails(pendingOrder);
    }
  }, [loading, pendingOrder]);

  const order = useMemo(() => {
    if (!routeOrder && !pendingDetails) return null;
    return {
      ...routeOrder,
      ...pendingDetails,
      otp: pendingDetails?.otp ?? routeOrder?.otp,
      expiresAt: pendingDetails?.expiresAt ?? routeOrder?.expiresAt,
      store: pendingDetails?.store || routeOrder?.store,
    };
  }, [routeOrder, pendingDetails]);

  const validTill = useMemo(() => {
    if (!order?.expiresAt) return "";
    return new Date(order.expiresAt).toLocaleString();
  }, [order]);

  const handleCancelOrder = () => {
    dispatch(
      cancelDiamondPointOrderRequest({
        id: order._id,
        onSuccess: () => navigate("/RedeemHistory"),
      }),
    );
  };

  const handleClose = () => {
    navigate("/RedeemHistory");
  };

  if (!order) {
    return <Typography>No Pending Order Found</Typography>;
  }

  return (
    <Box
      sx={{
        p: 2,
        background: themeColors.scaffoldGradientColors,
        minHeight: "100vh",
        border: `1px solid ${themeColors.scaffold}`,
        color: themeColors.text,
      }}
    >
      {/* Title */}
      <Typography
        sx={{
          color: themeColors.primary,
          textAlign: "center",
          fontSize: 22,
          fontWeight: 700,
          mb: 2,
          mt: 1,
        }}
      >
        Diamond Points
      </Typography>

      {/* MAIN CARD */}
      <Card
        sx={{
          border: `1px solid ${themeColors.scaffold}`,
          color: themeColors.text,
          background: themeColors.bigContainer,
          color: "white",
          borderRadius: 4,
          p: { xs: 2, sm: 3 },
          mt: 2,
          width: "100%",
          maxWidth: 700,
          mx: "auto",
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <CloseIcon
            sx={{ cursor: "pointer", color: themeColors.text }}
            onClick={handleClose}
          />
        </Box>
        {/* Pending Header */}
        <Box sx={{ textAlign: "center" }}>
          <AccessTimeIcon
            sx={{
              fontSize: 45,
              color: "#d2a700",
              background: "#3e3412",
              borderRadius: "50%",
              padding: 1,
              mb: 1,
            }}
          />

          <Typography
            sx={{
              fontSize: 22,
              fontWeight: 700,
              color: themeColors.text,
              background: themeColors.bigContainer,
            }}
          >
            Pending Order
          </Typography>

          <Typography
            sx={{
              mt: 1,
              fontSize: 14,
              opacity: 0.8,
              px: 4,
              lineHeight: 1.4,
            }}
          >
            Please redeem your coin at the store in the given time. Order Valid
            till {validTill}
          </Typography>
        </Box>

        <Divider sx={{ background: themeColors.text, my: 3 }} />

        {/* Points Section */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            color: themeColors.text,
            background: themeColors.bigContainer,
          }}
        >
          <Typography sx={{ fontWeight: "bold" }}>Points</Typography>
          <Typography sx={{ fontWeight: "bold" }}>
            {order.pointsRedeemed} <DiamondIcon />
          </Typography>
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            mt: 2,
          }}
        >
          <Typography sx={{ fontWeight: "bold" }}>Amount</Typography>
          <Typography sx={{ fontWeight: "bold" }}>{symbol} {order.amount}</Typography>
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            mt: 2,
            fontWeight: "bold",
          }}
        >
          <Typography sx={{ fontWeight: "bold" }}>
            Share OTP at store
          </Typography>
          <Box
            sx={{
              background: themeColors.primary,
              px: 2,
              py: 0.7,
              borderRadius: 2,
              fontWeight: "bold",
              color: "white",
              fontSize: 14,
            }}
          >
            {order.otp}
          </Box>
        </Box>

        <Card
          sx={{
            mt: 3,
            p: 2,
            borderRadius: 3,
            border: `1px solid ${themeColors.scaffold}`,
            color: themeColors.text,
            background: themeColors.bigContainer,
          }}
        >
          {/* Store details */}
          <Typography sx={{ fontSize: 20, fontWeight: 700 }}>
            {order.store?.branchName}
          </Typography>

          <Typography sx={{ mt: 0.5 }}>Phone: {order.store?.phone}</Typography>
          <Typography>Code: {order.store?.branchCode}</Typography>

          <Divider sx={{ my: 1, background: themeColors.text }} />

          <Typography sx={{ fontSize: 14 }}>
            {order.store?.address1}, {order.store?.address2},{" "}
            {order.store?.address3}
          </Typography>
        </Card>

        {/* Cancel Button */}
        <Button
          variant="contained"
          fullWidth
          sx={{
            mt: 4,
            py: 1.4,
            fontSize: 16,
            background: themeColors.primary,
            color: themeColors.text,
            "&:hover": {
              background: themeColors.svgColor,
            },
            border: `1px solid ${themeColors.scaffold}`,
            borderRadius: 3,
          }}
          onClick={handleCancelOrder}
        >
          Cancel Order
        </Button>
      </Card>
    </Box>
  );
}
